import { useCompanyLabels } from '../context/CompanyContext';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '../components/atoms/Button';
import Loading from '../components/atoms/Loading';
import { getFormFields } from '../redux/complaint/actions';
import { createComplaint } from '../redux/createComplaint/actions';
import { isStatusField, toUiFieldType } from '../data/formFieldPresets';
import { useDispatch } from 'react-redux';
import './complaintmanagement.css';
import '../components/organisms/settings-steps/review.css';
import StoreSelect from '../components/molecules/StoreSelect';
import FileUploadField from '../components/molecules/FileUploadField';
import { isStoreIdField } from '../data/formFieldPresets';
import { s3FileUpload } from '../redux/s3/actions';

const FIELD_TYPE_MAP = {
	CUSTOM: 'Custom',
	TEXT: 'Text',
	TEXTAREA: 'Text Area',
	SELECT: 'Select',
	DROPDOWN: 'Dropdown',
	NUMBER: 'Number',
	DATE: 'Date',
	DATETIME: 'Date Time',
	EMAIL: 'Email',
	CHECKBOX: 'Checkbox',
	RADIO: 'Radio',
	FILE: 'File',
	IMAGES: 'Images',
	TIME: 'Time',
};

function optionLabel(value) {
	if (typeof value === 'string') return value;
	return value?.label ?? '';
}

function normalizeFormFields(list = []) {
	const sorted = [...list].sort(
		(a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0),
	);

	return sorted.map((item) => {
		const label = item.fieldName ?? item.fieldLabel ?? item.label ?? '';
		const rawKey = item.fieldKey ?? String(item.id);
		const values = item?.options?.values ?? [];
		const parent = sorted.find((field) => (
			field.id === item.dependsOnFieldId
			|| (item.dependsOnFieldKey && field.fieldKey === item.dependsOnFieldKey)
		));
		return {
			id: item.id,
			label,
			fieldKey: isStatusField({ fieldKey: rawKey, label }) ? 'status' : rawKey,
			fieldType: toUiFieldType(item.fieldType, FIELD_TYPE_MAP, item.fieldKey),
			required: Boolean(item.required),
			dependsOn: item.dependsOnField?.fieldName ?? parent?.fieldName ?? item.dependsOn ?? '',
			options: values.map(optionLabel).filter(Boolean),
			parentMappings: Object.fromEntries(
				values
					.map((value) => {
						const option = optionLabel(value);
						const mapped = optionDependsOnValue(value);
						if (!option || !mapped) return null;
						return [option, resolveParentOptionLabel(mapped, parent?.options?.values ?? [])];
					})
					.filter(Boolean),
			),
		};
	});
}

function toFieldKey(label) {
	const words = String(label || '')
		.trim()
		.split(/[^A-Za-z0-9]+/)
		.filter(Boolean);
	if (!words.length) return '';
	return (
		words[0].toLowerCase() +
		words
			.slice(1)
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join('')
	);
}

function apiFieldKey(field) {
	if (isStatusField(field)) return 'status';
	const key = String(field.fieldKey || '').trim();
	if (key && !/\s/.test(key)) return key;
	return toFieldKey(field.label || key);
}

function optionDependsOnValue(value) {
	if (!value || typeof value !== 'object') return '';
	return value.dependsOnValue ?? value.parent ?? '';
}

function resolveParentOptionLabel(dependsOnValue, parentValues = []) {
	if (!dependsOnValue) return '';
	const parentLabels = parentValues.map(optionLabel).filter(Boolean);
	const exact = parentLabels.find((label) => label === dependsOnValue);
	if (exact) return exact;
	const byKey = parentLabels.find((label) => (
		toFieldKey(label) === dependsOnValue || toFieldKey(label) === toFieldKey(dependsOnValue)
	));
	return byKey || dependsOnValue;
}

function visibleOptionsFor(field, fields = [], values = {}) {
	const options = field.options || [];
	const dependsOn = String(field.dependsOn || '').trim();
	if (!dependsOn) return options;
	const parent = fields.find((candidate) => (
		String(candidate.label || '').trim() === dependsOn ||
		String(candidate.fieldKey || '').trim() === dependsOn
	));
	if (!parent) return options;
	const mappings = field.parentMappings || {};
	const hasMapping = options.some((option) => Boolean(mappings[option]));
	if (!hasMapping) return options;
	const parentValue = String(values[fieldName(parent)] || '').trim();
	if (!parentValue) return options;
	return options.filter((option) => String(mappings[option] || '') === parentValue);
}

// function applyParentAutoSelect(field, nextValue, fields, currentValues) {
// 	const dependsOn = String(field.dependsOn || '').trim();
// 	if (!dependsOn) return {};
// 	const parent = fields.find((candidate) => (
// 		String(candidate.label || '').trim() === dependsOn ||
// 		String(candidate.fieldKey || '').trim() === dependsOn
// 	));
// 	if (!parent) return {};
// 	const mappings = field.parentMappings || {};
// 	const selectedOptions = Array.isArray(nextValue) ? nextValue : [nextValue];
// 	const mappedParentValue = selectedOptions
// 		.map((option) => mappings[option])
// 		.find((mapped) => mapped);
// 	if (!mappedParentValue) return {};
// 	const parentKey = fieldName(parent);
// 	if (currentValues[parentKey] === mappedParentValue) return {};
// 	return { [parentKey]: mappedParentValue };
// }

function isMultiValueField(field) {
	return fieldType(field) === 'checkbox';
}

function findFieldByLabel(fields, label) {
	const target = String(label || '').trim();
	if (!target) return null;
	return fields.find((candidate) => (
		String(candidate.label || '').trim() === target ||
		String(candidate.fieldKey || '').trim() === target
	)) || null;
}

function findChildField(fields, field) {
	const label = String(field?.label || '').trim();
	if (!label) return null;
	return fields.find((candidate) => String(candidate.dependsOn || '').trim() === label) || null;
}

// Walks the entire dependsOn chain in both directions from the field that
// just changed, auto-selecting every linked ancestor and descendant option.
// Only follows a single representative value at each step (for checkbox
// fields with multiple selections, the first selected option is used to
// determine the cascade path).
function cascadeFieldSelection(changedField, nextValue, fields) {
	const updates = {};
	const primaryValue = Array.isArray(nextValue) ? nextValue[0] : nextValue;
	if (!primaryValue) return updates;

	// Upward: this field's parent, grandparent, etc.
	let curField = changedField;
	let curValue = primaryValue;
	while (curField?.dependsOn) {
		const parentField = findFieldByLabel(fields, curField.dependsOn);
		if (!parentField) break;
		const parentValue = (curField.parentMappings || {})[curValue];
		if (!parentValue) break;
		updates[fieldName(parentField)] = isMultiValueField(parentField) ? [parentValue] : parentValue;
		curField = parentField;
		curValue = parentValue;
	}

	// Downward: this field's child, grandchild, etc.
	curField = changedField;
	curValue = primaryValue;
	while (curField) {
		const childField = findChildField(fields, curField);
		if (!childField) break;
		const mappings = childField.parentMappings || {};
		const matchedOption = Object.keys(mappings).find((opt) => mappings[opt] === curValue);
		if (!matchedOption) break;
		updates[fieldName(childField)] = isMultiValueField(childField) ? [matchedOption] : matchedOption;
		curField = childField;
		curValue = matchedOption;
	}

	return updates;
}

function fieldName(field) {
	return String(field.fieldKey || field.id || field.label);
}

function fieldType(field) {
	const type = String(field.fieldType || '').toLowerCase();
	if (type === 'textarea' || type === 'text area') return 'textarea';
	if (type === 'date') return 'date';
	if (type === 'date time' || type === 'datetime' || type === 'datetime-local') return 'datetime-local';
	if (type === 'time') return 'time';
	if (type === 'email') return 'email';
	if (type === 'number' || type === 'mobile') return 'number';
	if (type === 'radio') return 'radio';
	if (type === 'checkbox' || type === 'multiselect') return 'checkbox';
	if (type === 'file') return 'file';
	if (type === 'images') return 'images';
	if (type === 'dropdown' || type === 'select' || type === 'custom') {
		return field.options.length ? 'select' : 'text';
	}
	return 'text';
}

function questionId(field) {
	return `new-query-${field.id ?? fieldName(field)}`;
}

function initialValue(field) {
	if (isStatusField(field)) {
		const open = (field.options || []).find((item) => String(item).trim().toLowerCase() === 'open');
		return open || 'Open';
	}
	const type = fieldType(field);
	if (type === 'checkbox' || type === 'file' || type === 'images') return [];
	return '';
}

function fieldValue(value) {
	if (Array.isArray(value)) {
		return value.map((item) => (
			typeof File !== 'undefined' && item instanceof File ? item.name : item
		));
	}
	if (typeof File !== 'undefined' && value instanceof File) return value.name;
	if (value == null) return '';
	return value;
}

function storedUploadValue(uploaded, file) {
	if (uploaded == null) return file?.name || '';
	if (typeof uploaded === 'string' || typeof uploaded === 'number') return String(uploaded);
	const attachment = uploaded.file && typeof uploaded.file === 'object'
		? uploaded.file
		: uploaded;
	return attachment.url
		|| attachment.fileUrl
		|| attachment.urlPath
		|| attachment.filePath
		|| attachment.path
		|| attachment.key
		|| attachment.fileName
		|| attachment.name
		|| uploaded.url
		|| uploaded.fileUrl
		|| uploaded.urlPath
		|| uploaded.filePath
		|| uploaded.path
		|| uploaded.key
		|| uploaded.fileName
		|| attachment.id
		|| uploaded.id
		|| file?.name
		|| '';
}

async function uploadedFieldValue(dispatch, field, value) {
	const type = fieldType(field);
	if (type === 'file' && typeof File !== 'undefined' && value instanceof File) {
		const uploaded = await dispatch(s3FileUpload({ file: value, folder: 'complaints' })).unwrap();
		return storedUploadValue(uploaded, value);
	}
	if (type === 'file' && Array.isArray(value)) {
		return Promise.all(value.map(async (item) => {
			if (typeof File !== 'undefined' && item instanceof File) {
				const result = await dispatch(s3FileUpload({ file: item, folder: 'complaints' })).unwrap();
				return storedUploadValue(result, item);
			}
			return item;
		}));
	}
	if (type === 'images' && Array.isArray(value)) {
		const uploaded = await Promise.all(value.map(async (item) => {
			if (typeof File !== 'undefined' && item instanceof File) {
				const result = await dispatch(s3FileUpload({ file: item, folder: 'complaints' })).unwrap();
				return storedUploadValue(result, item);
			}
			return item;
		}));
		return uploaded;
	}
	return fieldValue(value);
}

async function buildFieldValues(fields = [], values = {}, dispatch) {
	const fieldValues = {};
	for (const field of fields) {
		const key = apiFieldKey(field);
		if (!key) continue;
		fieldValues[key] = await uploadedFieldValue(dispatch, field, values[fieldName(field)]);
	}
	return fieldValues;
}

function isEmptyValue(value) {
	if (Array.isArray(value)) return value.length === 0;
	if (typeof File !== 'undefined' && value instanceof File) return false;
	return value == null || String(value).trim() === '';
}

function isMobileValue(value) {
	return /^\d+$/.test(String(value).trim());
}

function validateFieldValues(fields = [], values = {}) {
	for (const field of fields) {
		const type = fieldType(field);
		const value = values[fieldName(field)];
		const label = field.label || 'This field';
		if (field.required && isEmptyValue(value)) return `${label} is required.`;
		if (isEmptyValue(value)) continue;
		if (type === 'number' && !isMobileValue(value)) return `${label} must be a valid number.`;
	}
	return '';
}

function NewComplaintControl({ field, value, onChange, storeList, options = field.options }) {
  const { fieldLabel } = useCompanyLabels();
	const type = fieldType(field);
	const id = questionId(field);
	const locked = isStatusField(field);

	if (isStoreIdField(field) && Array.isArray(storeList)) {
		return <StoreSelect id={id} name={fieldName(field)} required={field.required} storeList={storeList} value={value} onChange={onChange} />;
	}

	if (type === 'radio') {
		return (
			<div className="review-options review-options--inline">
				{options.map((option) => (
					<label className="review-option" htmlFor={`${id}-${option}`} key={option}>
						<input
							id={`${id}-${option}`}
							name={fieldName(field)}
							type="radio"
							value={option}
							checked={value === option}
							onChange={(event) => onChange(event.target.value)}
							required={field.required}
							disabled={locked}
						/>
						<span>{option}</span>
					</label>
				))}
			</div>
		);
	}

	if (type === 'checkbox') {
		const selected = Array.isArray(value) ? value : [];
		return (
			<div className="review-options review-options--inline">
				{options.map((option) => (
					<label className="review-option" htmlFor={`${id}-${option}`} key={option}>
						<input
							id={`${id}-${option}`}
							name={`${fieldName(field)}[]`}
							type="checkbox"
							value={option}
							checked={selected.includes(option)}
							onChange={(event) => onChange(
								event.target.checked
									? [...selected, option]
									: selected.filter((item) => item !== option),
							)}
						/>
						<span>{option}</span>
					</label>
				))}
			</div>
		);
	}

	if (type === 'textarea') {
		return <textarea id={id} name={fieldName(field)} className="review-control review-control--textarea" value={value} onChange={(event) => onChange(event.target.value)} required={field.required} disabled={locked} />;
	}

	if (type === 'file') {
		return (
			<FileUploadField
				id={id}
				name={fieldName(field)}
				value={value}
				onChange={onChange}
				multiple
				required={field.required}
				disabled={locked}
			/>
		);
	}

	if (type === 'images') {
		return (
			<FileUploadField
				id={id}
				name={fieldName(field)}
				value={value}
				onChange={onChange}
				multiple
				required={field.required}
				disabled={locked}
			/>
		);
	}

	if (type === 'number') {
		return (
			<input
				id={id}
				name={fieldName(field)}
				className="review-control"
				type="number"
				inputMode="numeric"
				value={value}
				onChange={(event) => onChange(String(event.target.value).replace(/[^\d]/g, ''))}
				onKeyDown={(event) => {
					if (event.key === 'e' || event.key === 'E' || event.key === '+' || event.key === '-' || event.key === '.') {
						event.preventDefault();
					}
				}}
				required={field.required}
				disabled={locked}
			/>
		);
	}

	if (type === 'select') {
		const selectOptions = (options || []).includes(value) || !locked || !value
			? options
			: [value, ...options];
		return (
			<select id={id} name={fieldName(field)} className="review-control" value={value} onChange={(event) => onChange(event.target.value)} required={field.required} disabled={locked}>
				{locked ? null : <option value="">Select {fieldLabel(field)}</option>}
				{selectOptions.map((option) => <option key={option} value={option}>{option}</option>)}
			</select>
		);
	}

	return <input id={id} name={fieldName(field)} className="review-control" type={type} value={value} onChange={(event) => onChange(event.target.value)} required={field.required} disabled={locked} />;
}

export default function NewComplaint({ storeList } = {}) {
  const { fieldLabel } = useCompanyLabels();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { complaintTypeId } = useParams();
	const complaintId = Number(complaintTypeId);
	const hasComplaintTypeId = Number.isFinite(complaintId) && complaintId > 0;
		const [fields, setFields] = useState([]);
		const [values, setValues] = useState({});
		const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [complaintName, setComplaintName] = useState('');

	useEffect(() => {
		if (!hasComplaintTypeId) {
			return undefined;
		}
		let cancelled = false;
		dispatch(getFormFields(complaintId))
			.unwrap()
			.then((response) => {
				if (cancelled) return;
				const nextFields = normalizeFormFields(Array.isArray(response?.data) ? response.data : response);
				setFields(nextFields);
				setValues(Object.fromEntries(nextFields.map((field) => [fieldName(field), initialValue(field)])));
			})
			.catch((requestError) => {
				if (!cancelled) setError(typeof requestError === 'string' ? requestError : requestError?.message || 'Failed to load form fields.');
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});
		return () => { cancelled = true; };
	}, [complaintId, dispatch, hasComplaintTypeId]);

	const hasFields = useMemo(() => fields.length > 0, [fields]);

	const handleSubmit = async (event) => {
		event.preventDefault();
		if (!hasComplaintTypeId) {
			setError('A valid complaint type is required.');
			return;
		}
		setError('');
		setSuccess('');
		setIsSaving(true);
		if (!complaintName.trim()) {
			setError('Complaint Name is required.');
			setIsSaving(false);
			return;
		}
		const validationError = validateFieldValues(fields, values);
		if (validationError) {
			setError(validationError);
			setIsSaving(false);
			return;
		}
		try {
			const fieldValues = await buildFieldValues(fields, values, dispatch);
			const payload = {
				complaintTypeId: complaintId,
				title: complaintName.trim(),
				fieldValues,
			};
			await dispatch(createComplaint(payload)).unwrap();
			setSuccess('Query submitted successfully.');
			navigate(`/complaintmanagement/${complaintId}`);
		} catch (requestError) {
			setError(typeof requestError === 'string' ? requestError : requestError?.message || 'Failed to submit query.');
		} finally {
			setIsSaving(false);
		}
	};

	const renderField = (field) => {
		const options = visibleOptionsFor(field, fields, values);
		return (
			<div className={`review-question${['textarea', 'radio', 'checkbox'].includes(fieldType(field)) ? ' review-question--wide' : ''}`} key={field.id ?? fieldName(field)}>
				<label className="review-label" htmlFor={questionId(field)}>
					{fieldLabel(field) || 'Untitled field'}
					{field.required ? <span className="review-required">*</span> : null}
				</label>
				{/* <NewComplaintControl
					storeList={storeList}
					field={field}
					options={options}
					value={values[fieldName(field)] ?? initialValue(field)}
					onChange={(value) => {
						if (isStatusField(field)) return;
						setValues((current) => {
							const parentUpdate = applyParentAutoSelect(field, value, fields, current);
							return { ...current, [fieldName(field)]: value, ...parentUpdate };
						});
					}}
				/> */}
								<NewComplaintControl
					storeList={storeList}
					field={field}
					options={options}
					value={values[fieldName(field)] ?? initialValue(field)}
					onChange={(value) => {
						if (isStatusField(field)) return;
						setValues((current) => {
							const cascadeUpdates = cascadeFieldSelection(field, value, fields);
							return { ...current, [fieldName(field)]: value, ...cascadeUpdates };
						});
					}}
				/>
			</div>
		);
	};

	const renderComplaintNameField = () => (
		<div className="review-question" key="complaint-name">
			<label className="review-label" htmlFor="new-query-complaint-name">
				Complaint Name
			</label>
			<input
				id="new-query-complaint-name"
				name="complaintName"
				className="review-control"
				type="text"
				value={complaintName}
				onChange={(event) => setComplaintName(event.target.value)}
			/>
		</div>
	);

	return (
		<main className="ca-query-management-page">
			<section className="ca-query-details" aria-labelledby="new-query-title">
				<div className="ca-query-details__heading">
					<div className="ca-query-details__title-row">
						<button
							type="button"
							className="ca-settings-page__back"
							onClick={() => navigate(`/complaintmanagement/${complaintId}`)}
							aria-label="Back to Complaint Management"
						>
							<svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
								<path
									d="M15 6l-6 6 6 6"
									stroke="currentColor"
									strokeWidth="2.2"
									strokeLinecap="round"
									strokeLinejoin="round"
								/>
							</svg>
						</button>
						<h1 id="new-query-title" className="ca-query-details__title">New Complaint</h1>
					</div>
				</div>
				{(error || !hasComplaintTypeId) ? (
					<div className="ca-validation-toast" role="alert">{error || 'A valid complaint type is required.'}</div>
				) : null}
				{isLoading && hasComplaintTypeId ? <Loading text="Loading form fields" /> : null}
				{hasComplaintTypeId && !isLoading && hasFields ? (
					<form className="review-form-card" onSubmit={handleSubmit}>
						<div className="review-form-grid">
							{fields.map((field) => (
								<React.Fragment key={field.id ?? fieldName(field)}>
									{renderField(field)}
									{isStatusField(field) ? renderComplaintNameField() : null}
								</React.Fragment>
							))}
							{!fields.some(isStatusField) ? renderComplaintNameField() : null}
						</div>
						<div className="ca-new-query__actions">
							<Button type="submit" disabled={isSaving}>{isSaving ? 'Submitting...' : 'Submit Complaint'}</Button>
						</div>
						{success ? <p className="ca-body">{success}</p> : null}
					</form>
				) : null}
			</section>
		</main>
	);
}